import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  RefreshAuthBody,
  RefreshAuthData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface RefreshAuthState
  extends MergeRequestState<RefreshAuthData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.refreshAuth,
  isFetching: (state: ReduxState) => state.server.refreshAuth.isFetching,
  data: (state: ReduxState) => state.server.refreshAuth.data,
  error: (state: ReduxState) => state.server.refreshAuth.error,
};

const initialState: RefreshAuthState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/refreshAuth';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { body, config }: { body: RefreshAuthBody; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.refreshAuth(body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerRefreshAuth {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const refreshAuth: ServerRefreshAuth = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
